import React, { useEffect, useState } from "react";

import {
  calculateFootprintingProgress,
  formatNumericCurrency,
  isPercentageComplete,
  queryIsLoading,
  triggerTrengo,
  useGetFootprintingCropFootprints,
  useGetFootprintingFarmFootprint,
  useNavigate,
  useTranslation,
} from "utils";

import { CarbonProgram } from "models";

import { Button, Card, Col, Drawer, Flex, Icon, Link, Row, Skeleton, Tooltip } from "components";

import FootprintingOnboardingSteps from "pages/app/_app/components/FootprintingOnboardingSteps";
import FootprintingSubmissionModal from "pages/app/footprinting/components/FootprintingSubmissionModal";

interface FootprintingProgressWidgetProps {
  farmId: string;
  year: number;
  carbonProgram: CarbonProgram;
}

const FootprintingProgressWidget = ({ farmId, year, carbonProgram }: FootprintingProgressWidgetProps) => {
  const { t } = useTranslation("footprinting");
  const navigate = useNavigate();
  const [totalPayout, setTotalPayout] = useState<number>(0);
  const [formsSubmitted, setFormsSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [trengo, setTrengo] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [onboardingDrawerVisable, setOnboardingDrawerVisable] = useState(false);

  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year);
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, [year], carbonProgram.farmLevelFootprinting?.programCrops);
  const showPayouts = carbonProgram.platformFeatures?.footprinting?.payouts ?? true;

  useEffect(() => {
    if (farmProgressQuery.data && carbonProgram.farmLevelFootprinting?.programCrops.length === cropProgressQueries.length) {
      const { totalPayout } = calculateFootprintingProgress(
        farmProgressQuery.data,
        cropProgressQueries.filter((cpq) => !!cpq.data).map((cpq) => cpq.data)
      );

      setTotalPayout(totalPayout);
      setFormsSubmitted(cropProgressQueries.some((form) => form.data?.footprint?.isSubmitted) || !!farmProgressQuery.data.footprint?.isSubmitted);
    }
  }, [farmProgressQuery.data, cropProgressQueries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading([...cropProgressQueries, farmProgressQuery].some(queryIsLoading));
  }, [farmProgressQuery, cropProgressQueries]);

  if (loading) {
    return (
      <Card className="padding-125 width-100 margin-top-100 footprint-progress-widget">
        <Flex className="skeleton" vertical={true}>
          <div className="skeleton-containers">
            <Skeleton type="block" width="8.875rem" height="1.125rem" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers margin-bottom-150">
            <Skeleton type="block" height="11.875rem" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers">
            <Skeleton type="block" width="16.5rem" height="0.625rem" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers margin-bottom-50">
            <Skeleton type="block" width="8.875rem" height="2.375rem" className="skeleton-rectangular" />
          </div>
          <Row justify="space-between" className="padding-0">
            <Col sm={12} className="skeleton-containers">
              <Skeleton type="block" width="8.875rem" height="1.5rem" className="skeleton-rectangular" />
            </Col>
            <Col sm={3} className="skeleton-containers padding-0">
              <Skeleton type="block" width="2.125rem" height="0.875rem" className="skeleton-rectangular" />
            </Col>
          </Row>
          <div className="skeleton-containers">
            <Skeleton type="block" height="0.625rem" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers">
            <Skeleton type="block" height="3rem" className="skeleton-rectangular" />
          </div>
          <Flex className="skeleton-containers">
            <Skeleton type="block" width="13.625rem" height="1.25rem" className="skeleton-rectangular centered" />
          </Flex>
        </Flex>
      </Card>
    );
  }

  const readyToSubmit =
    isPercentageComplete(farmProgressQuery.data?.completionPct ?? 0) && cropProgressQueries.some((cpq) => isPercentageComplete(cpq.data?.completionPct ?? 0));

  const handleSubmitClick = () => {
    setShowSubmitModal(true);
  };

  const handleModalConfirm = () => {
    setShowSubmitModal(false);
    navigate(`review/${year}`);
  };

  const handleModalCancel = () => {
    setShowSubmitModal(false);
  };

  const handleOnboardingBegin = () => {
    setOnboardingDrawerVisable(false);
  };

  return (
    <>
      <Flex className="width-100 flex footprint-progress-widget container" vertical>
        <Flex className="padding-100 footprinting-sidebar-container" vertical>
          <Flex align="center" justify="center" className="flex-1">
            <Card className="margin-top-auto flex-center">
              <Flex align="center" justify="center" vertical gap={8}>
                <Icon size={72} iconName="klim/crop-footprint-blue" className="margin-bottom-100 margin-top-100" />
                <p className="margin-top-0 margin-bottom-0 paragraph-l centered-text">{t("progressWidget.understand")}</p>
                <p className="margin-top-0 margin-bottom-200 paragraph-m centered-text">{t("progressWidget.title")}</p>
              </Flex>
              <hr className="dashed-hr" />
              <Flex vertical gap={12}>
                <Flex justify="flex-start" align="flex-start" className="margin-top-150">
                  <p className="paragraph-s margin-0 step-number">1</p>
                  <p className="paragraph-m margin-0 margin-left-50">{t("progressWidget.bullet.collect")}</p>
                </Flex>
                <Flex justify="flex-start" align="flex-start">
                  <p className="paragraph-s margin-0 step-number">2</p>
                  <p className="paragraph-m margin-0 margin-left-50">{t("progressWidget.bullet.complete")}</p>
                </Flex>
                <Flex justify="flex-start" align="flex-start">
                  <p className="paragraph-s margin-0 step-number">3</p>
                  <p className="paragraph-m margin-0 margin-left-50">{t("progressWidget.bullet.results")}</p>
                </Flex>
                <Flex justify="flex-start" align="flex-start">
                  <p className="paragraph-s margin-0 step-number">4</p>
                  <p className="paragraph-m margin-0 margin-left-50">{t("progressWidget.bullet.review")}</p>
                </Flex>
                {showPayouts && (
                  <Flex justify="flex-start" align="flex-start">
                    <p className="paragraph-s margin-0 step-number">5</p>
                    <p className="paragraph-m margin-0 margin-left-50">{t("progressWidget.bullet.verified")}</p>
                  </Flex>
                )}
                <Flex justify="flex-start" align="center" className="margin-left-150 margin-bottom-150">
                  <Link className="margin-left-25" onClick={() => setOnboardingDrawerVisable(true)}>
                    {t("progressWidget.learnMore")}
                  </Link>
                </Flex>
              </Flex>
            </Card>
          </Flex>
        </Flex>
        <hr className="margin-0 margin-bottom-200 dashed-hr margin-left-100 margin-right-100" />
        <div className="padding-100">
          {showPayouts && (
            <Flex className="margin-bottom-100" justify="space-between" align="center">
              <p className="margin-top-0 margin-bottom-0 paragraph-s grey-600-text">{t("progressWidget.payout")}</p>
              <h4 className="green-text">
                {t("progressWidget.upto")} {formatNumericCurrency(totalPayout)}
              </h4>
            </Flex>
          )}
          {formsSubmitted ? (
            <div className="margin-bottom-100 padding-100 submitted-payment">
              <Flex align="center">
                <Icon iconName="tabler/circle-check-filled" className="margin-right-50 green-text" />
                <h6>{t("progressWidget.wellDone")}</h6>
              </Flex>
              <p className="paragraph-s margin-top-100 margin-bottom-100">{t("progressWidget.payoutSubmitted")}</p>
              <Flex>
                <span className="margin-0">
                  {t("reviewAndSubmit.support")}
                  <Link className="margin-left-25" onClick={() => triggerTrengo("email", trengo, setTrengo)}>
                    {t("reviewAndSubmit.contact")}
                  </Link>
                </span>
              </Flex>
            </div>
          ) : (
            <div>
              <p className="paragraph-s margin-top-100 margin-bottom-100">{t("progressWidget.payoutProgress")}</p>
              {!readyToSubmit ? (
                <Tooltip title={t("progressWidget.button.completion")} style={{ display: "flex", alignItems: "center" }}>
                  <Button type="primary" className="margin-bottom-100" disabled={true} onClick={() => navigate(`review/${year}`)}>
                    {t("progressWidget.submitButton")}
                  </Button>
                </Tooltip>
              ) : (
                <Button type="primary" className="margin-bottom-100" disabled={false} onClick={handleSubmitClick}>
                  {t("progressWidget.submitButton")}
                </Button>
              )}
            </div>
          )}
        </div>
      </Flex>
      <FootprintingSubmissionModal open={showSubmitModal} onConfirm={handleModalConfirm} onCancel={handleModalCancel} />
      <Drawer
        data-testid="footprinting-onboarding-drawer"
        title={t("onboarding.howToAssessTitle")}
        visible={onboardingDrawerVisable}
        onClose={() => setOnboardingDrawerVisable(false)}
        destroyOnClose
      >
        <FootprintingOnboardingSteps carbonProgram={carbonProgram} onBegin={handleOnboardingBegin} />
      </Drawer>
    </>
  );
};

export default FootprintingProgressWidget;
